import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../../../helpers/api';
import { ENDPOINTS, FILTER_OPERATORS } from '../../../../helpers/constants';
import { getUserFromLocalstrorage } from '../../../../helpers/global';
import DataTable from '../../../common/data-table/DataTable';
import { getOrderTableColumns, pickOrderColumnsByKeys } from '../../../orders/orderTableColumns';

const ORDER_TYPES = ['Lien Search', 'Estoppel', 'Survey'];
const TASK_ORDER_PICKER_COLUMN_KEYS = ['propertyAddress', 'fileName', 'orderType', 'status', 'createdAt', 'accurateId', 'client', 'assignedProcessors.0.name'];

const TaskOrderPicker = ({ show, onHide, submit, selectedOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [activeOrder, setActiveOrder] = useState(selectedOrder);

  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const user = useMemo(() => getUserFromLocalstrorage(), []);
  const pageSize = 50;
  const columns = useMemo(() => {
    const allColumns = getOrderTableColumns({ user });
    return pickOrderColumnsByKeys(allColumns, TASK_ORDER_PICKER_COLUMN_KEYS);
  }, [user]);

  const fetchOrders = useCallback(
    async (
      page = 1,
      sortKey = 'createdAt',
      sortDirection = 'desc',
      search = '',
      append = false,
      orderTypeFilter = null
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      const { signal } = controller;
      abortControllerRef.current = controller;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Build filters with provided orderTypeFilters
      const filters = [];
      if (orderTypeFilter) {
        filters.push({
          key: 'orderType',
          value: [orderTypeFilter],
          operator: FILTER_OPERATORS.IS
        });
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.FETCH_ORDERS,
        method: 'POST',
        requestBody: {
          page,
          pageSize,
          sort: { [sortKey]: sortDirection === 'asc' ? 1 : -1 },
          query: search.trim(),
          filters
        },
        signal
      });

      if (error === ABORT_ERROR) return;

      if (error) {
        toast.error('Error while fetching orders.');
      } else if (response && response.results) {
        if (append) {
          setOrders(prevOrders => [...prevOrders, ...response.results]);
        } else {
          setOrders(response.results);
        }

        setTotalCount(response.resultCount);
        const totalPages = Math.ceil(response.resultCount / pageSize);
        setHasMoreData(page < totalPages);
      }

      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    if (show) {
      setCurrentPage(1);
      setHasMoreData(true);
      fetchOrders(1, sortConfig.key, sortConfig.direction, '', false, selectedOrderType);
    }
  }, [show, sortConfig, selectedOrderType, fetchOrders]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setSearchQuery('');
      setSelectedOrderType(null);
      setOrders([]);
    } else {
      // When modal opens, set the active order to the selected order
      setActiveOrder(selectedOrder);
    }
  }, [show, selectedOrder]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = query => {
    setSearchQuery(query);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      setHasMoreData(true);
      fetchOrders(1, sortConfig.key, sortConfig.direction, query, false, selectedOrderType);
    }, 500);
  };

  const handleSortChange = newSortConfig => {
    setSortConfig(newSortConfig);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchOrders(nextPage, sortConfig.key, sortConfig.direction, searchQuery, true, selectedOrderType);
    }
  };

  const handleOrderTypeToggle = orderType => {
    setSelectedOrderType(prev => {
      if (prev === orderType) {
        return null;
      }
      return orderType;
    });
  };

  const handleRowClick = row => {
    setActiveOrder(row === activeOrder ? null : row);
  };

  const handleSubmit = () => {
    if (activeOrder) {
      submit(activeOrder);
      onHide();
    } else {
      toast.error('Please select an order');
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      backdrop="static"
      size="xl"
      dialogClassName="task-order-picker-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Order for Task</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-3 px-3">
        <Row className="mb-3">
          <Col md={12}>
            <div className="d-flex align-items-center mb-3" style={{ gap: '8px' }}>
              <div className="position-relative" style={{ flex: 1 }}>
                <Search
                  className="icon-md position-absolute"
                  style={{
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6c757d'
                  }}
                />
                <Form.Control
                  type="text"
                  size="md"
                  className="bg-white search-input text-dark"
                  placeholder="Search orders by address, file name, service..."
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  style={{ paddingLeft: 40, border: '1px solid #e5e7ea' }}
                />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
              <span className="text-muted small font-weight-semibold">Order Type:</span>
              {ORDER_TYPES.map(orderType => {
                const isSelected = selectedOrderType === orderType;
                const serviceColors = {
                  'Lien Search': '#f59e0b',
                  'Estoppel': '#a855f7',
                  'Survey': '#10b981'
                };
                const bgColor = serviceColors[orderType] || '#6c757d';
                
                return (
                  <Button
                    key={orderType}
                    type="button"
                    size="sm"
                    onClick={() => handleOrderTypeToggle(orderType)}
                    className="shadow-none"
                    style={{
                      backgroundColor: isSelected ? bgColor : 'transparent',
                      borderColor: bgColor,
                      color: isSelected ? 'white' : bgColor,
                      fontWeight: isSelected ? '600' : '500',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {orderType}
                  </Button>
                );
              })}
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <DataTable
              data={orders}
              columns={columns}
              rowKey="_id"
              pagination={false}
              sortable={true}
              defaultSort={sortConfig}
              onSortChange={handleSortChange}
              striped={false}
              hover={true}
              bordered={false}
              responsive={false}
              maxHeight="50vh"
              loading={loading}
              emptyMessage="No orders found"
              onBottomReached={handleLoadMore}
              loadingMoreData={loadingMore}
              bottomOffset={100}
              onRowClick={handleRowClick}
              rowToHighlight={activeOrder?._id}
              highLightRowColor="info-light"
            />
            {totalCount > 0 && !loading && (
              <div className="text-muted mt-2 small">
                Showing <span className="text-dark font-weight-bold">{orders.length}</span> of{' '}
                <span className="text-dark font-weight-bold">{totalCount}</span> orders
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} className="mr-2">
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={!activeOrder}>
          Select Order
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskOrderPicker;
